import { AxiosResponse } from "axios";
import { createApi } from "../../../../utils/createApi";
import { CredentialSource, FetchedCredential } from "../vpnDebugInterfaces";

/**
 * What `password_filter_for_representation` substitutes for a real secret when
 * the user lacks `inframap.view_passwords`. Nine U+2022 bullets — matched
 * exactly, because prefilling it and posting it back would authenticate with
 * literal mask characters and read as a wrong password rather than as "nothing
 * was supplied". (The worker guards this too, but the UI must not offer it.)
 */
export const FAKE_PASSWORD = "•••••••••";

const isMasked = (value: string | null | undefined) => value === FAKE_PASSWORD;

const clean = (value: string | null | undefined) =>
  typeof value === "string" && value.trim() !== "" ? value : "";

interface CredentialPayload {
  username?: string | null;
  password?: string | null;
}

/**
 * The credentials plane B would use for a camera, resolved the same way the
 * server does.
 *
 * Mirrors `resolve_camera_target`: the camera's own secret wins, else the
 * site's, else nothing. Both are already exposed to camera-ui —
 * `camera/{id}/general_info/` and `customer/{id}/about/` both run through
 * `PasswordFilterBaseSerializer`, so the `view_passwords` gate is enforced
 * server-side and a user without it receives the mask rather than the secret.
 *
 * Resolving it client-side rather than adding an API field keeps this a
 * frontend change, and means the form can show which source is in play instead
 * of only whether the *camera* has one — which is what the debugger's own
 * `targets/` payload reports, and is misleading for a camera that inherits its
 * site's credential.
 */
export const fetchCameraCredentials = async (
  cameraId: number,
  customerId: number | null
): Promise<FetchedCredential> => {
  const AxiosUI = createApi("");

  let username = "";
  let password = "";
  let masked = false;
  let source: CredentialSource = "none";

  try {
    const response: AxiosResponse<CredentialPayload> = await AxiosUI.get(
      `camera/${cameraId}/general_info/`
    );
    const data = response.data || {};
    if (clean(data.username) || clean(data.password)) {
      username = clean(data.username);
      masked = isMasked(data.password);
      password = masked ? "" : clean(data.password);
      source = "camera";
    }
  } catch {
    // Non-fatal: fall through to the site, then to "none". The run still works
    // via camera_id, where the server resolves the secret itself.
  }

  if (source === "none" && customerId != null) {
    try {
      const response: AxiosResponse<CredentialPayload> = await AxiosUI.get(
        `customer/${customerId}/about/`
      );
      const data = response.data || {};
      if (clean(data.username) || clean(data.password)) {
        username = clean(data.username);
        masked = isMasked(data.password);
        password = masked ? "" : clean(data.password);
        source = "site";
      }
    } catch {
      // Same: leave it as "none".
    }
  }

  return { username, password, source, masked };
};
